import { useState } from 'react'
import { Box, Button, Collapse, Image, Paper, Stack, Text } from '@mantine/core'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { MarkdownContent } from './steps/MarkdownContent'

interface ProjectGuideOverviewProps {
  content?: string
  heroImageUrl?: string
  defaultExpanded?: boolean
  title?: string
}

const COLLAPSED_CHAR_LIMIT = 400

/**
 * Extract a clean text preview from markdown content
 */
function getTextPreview(markdown: string, limit: number): string {
  // Strip markdown formatting for preview
  const text = markdown
    // Remove headers
    .replace(/^#{1,4}\s+/gm, '')
    // Remove bold/italic
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    // Remove links, keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove list markers
    .replace(/^[-*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    // Collapse whitespace
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (text.length <= limit) return text
  
  // Cut at word boundary
  const cut = text.slice(0, limit)
  const lastSpace = cut.lastIndexOf(' ')
  return `${cut.slice(0, lastSpace > limit - 50 ? lastSpace : limit)}...`
}

export function ProjectGuideOverview({
  content,
  heroImageUrl,
  defaultExpanded = false,
  title,
}: ProjectGuideOverviewProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  if (!content && !heroImageUrl) {
    return null
  }

  const isLongContent = content && content.length > COLLAPSED_CHAR_LIMIT
  const previewText = content ? getTextPreview(content, COLLAPSED_CHAR_LIMIT) : ''

  return (
    <Paper id="overview" data-toc-section component="article" p="lg" withBorder radius="md">
      <Stack gap="md">

        {heroImageUrl && (
          <Image
            src={heroImageUrl}
            alt={title ? `${title} - DIY project preview` : 'DIY project preview'}
            radius="md"
            mah={300}
            fit="cover"
            loading="eager"
            style={{
              aspectRatio: '16/9',
              objectFit: 'cover',
            }}
          />
        )}

        {content && (
          <>
            {/* Collapsed preview */}
            {!expanded && isLongContent && (
              <Box>
                <Text size="sm" c="dimmed" lh={1.6}>
                  {previewText}
                </Text>
              </Box>
            )}

            {/* Full content */}
            <Collapse in={expanded || !isLongContent}>
              <MarkdownContent content={content} />
            </Collapse>

            {isLongContent && (
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                leftSection={expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Show less' : 'Read full overview'}
              </Button>
            )}
          </>
        )}
      </Stack>
    </Paper>
  )
}
